import React from "react"
import { GetReportedHoursQuery, useGetReportedHoursQuery } from "../api"
import { type ReportedHourViewModel } from "../api/reported-hours"
import { convertDecimalHoursToHoursAndMinutes } from "../utils"
import { formatDateRangeQuery } from "../components/DateRangeSelector/use-date-range"

const getTotalReportedHours = (reports: ReportedHourViewModel[]) => {
  const totalReportedHours = reports.reduce((total, report) => {
    const hours = report.durationInDecimalHours || 0
    return total + hours
  }, 0)

  return convertDecimalHoursToHoursAndMinutes(totalReportedHours)
}

export const useHourReports = ({ fromDate, toDate, membershipId }: UseHourReportsOptions) => {
  const queryParams = React.useMemo(
    () =>
      ({
        fromDate: formatDateRangeQuery(fromDate),
        ...(toDate ? { toDate: formatDateRangeQuery(toDate) } : {}),
        ...(membershipId ? { membershipId } : {}),
      } as GetReportedHoursQuery),
    [fromDate, membershipId, toDate]
  )

  const queryResult = useGetReportedHoursQuery(queryParams, {
    refetchOnMountOrArgChange: true,
  })
  const reports = queryResult.currentData ?? []
  const totalReportedHours = getTotalReportedHours(reports)

  return {
    reports,
    totalReportedHours,
    meta: {
      ...queryResult,
      isLoading: queryResult.isLoading || (queryResult.isFetching && !queryResult.currentData),
    },
  }
}

type UseHourReportsOptions = {
  fromDate: Date
  toDate?: Date
  membershipId: string | null
}
